/**
 * The wire reader: a batch of ops as it arrives (from the person's client or
 * a compiled change), read without trusting it into the strict `StudioOp[]`.
 *
 * The wire is the strict op with three differences:
 *
 *   every formula tree (`formula`, `startsWith`, `showWhen`, at every depth of
 *   lines) is a flat list (`FlatFormula`), read through `readDraftField`, so
 *   a problem names the field's path
 *   a blank string reads as absent, as the setup tool's schema reads it
 *   every array is capped here, by `STUDIO_LIMITS` and the builder's caps (a
 *   schema given to a model must not carry array caps)
 *
 * Every problem of every op is reported at once: OPS_NOT_LIST, OPS_TOO_MANY,
 * OP_UNKNOWN (naming the op) and OP_SHAPE (naming the member and what it must
 * be), each with its op's index.
 */
import { AGENT_LANGUAGE_TAGS, type AgentLanguageTag } from '../agent-language';
import { isCaptureTone, isRecordIconId, type RecordIconId } from '../api';
import {
  AUTHOR_TYPES,
  readDraftField,
  flattenDraftFormula,
  readDraftFormula,
  SETUP_AGGS,
  SETUP_FORMULA_CALC_OPS,
  SETUP_DIRECTIONS,
  SETUP_DRAFT_LIMITS,
  SETUP_FORMULA_RESULTS,
  SETUP_TILE_OPS,
  SETUP_TILE_SCOPES,
  type FlatFormula,
  type SetupDraftField,
  type SetupDraftFieldWire,
  type SetupDraftFormula,
  type SetupDraftRecord,
  type SetupDraftSection,
  type SetupDraftSeedRow,
} from '../compose-author';
import { UNMET_NEED_LEVELS } from '../compose';
import { isColumnTransform } from '../imports';
import { INSIGHT_WHERE_MAX_VALUES, INSIGHT_WHERE_OPS, isInsightCardId } from '../insights';
import { LINK_CONDITION_OPS, LINK_MODES } from '../links';
import { CAPABILITY_IDS } from '../manifest/ids';
import { ACCESS_LEVELS } from '../roles';
import { flattenBoardCardTotal, readBoardCardWire, STUDIO_CARD_WIRE_NODES, type StudioBoardCardWire, type StudioCardNodeWire } from './cards';
import type { StudioOpProblem } from './apply';
import {
  STUDIO_LIMITS,
  STUDIO_NOT_YET_KINDS,
  STUDIO_APP_MAP_INTENTS,
  STUDIO_APP_MAP_KINDS,
  STUDIO_APP_MAP_STATUSES,
  STUDIO_NOTE_KINDS,
  STUDIO_ROLE_BASES,
  STUDIO_SOURCE_ROLES,
  type CriticCheck,
  type StudioBoardCard,
  type StudioCardWhere,
  type StudioFrom,
  type StudioImport,
  type StudioLink,
  type StudioLinkCondition,
  type StudioLinkFill,
  type StudioNote,
  type StudioNotYet,
  type StudioAppMap,
  type StudioRole,
} from './draft';
import {
  STUDIO_FIELD_CLEARABLE,
  STUDIO_RECORD_CLEARABLE,
  type StudioFieldInput,
  type StudioFieldSet,
  type StudioOp,
  type StudioTileInput,
} from './ops';
import { STUDIO_KEY, type StudioPartRef } from './paths';

/** What an op reader answers: the op, or null when it said a problem. */
export type StudioOpReader = (raw: Record<string, unknown>, read: StudioWireReader) => StudioOp | null;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const blank = (value: unknown): boolean => value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/**
 * The reading toolkit one op is read with: each member reader says its own
 * problem (at `ops[<i>].<member>`) and answers `undefined` for an absent
 * optional member. `ok` turns false on the first problem.
 */
export class StudioWireReader {
  ok = true;
  constructor(
    readonly index: number,
    private readonly problems: StudioOpProblem[],
  ) {}

  bad(at: string, what: string): undefined {
    this.ok = false;
    this.problems.push({ code: 'OP_SHAPE', op: this.index, message: `ops[${this.index}].${at} must be ${what}.` });
    return undefined;
  }

  /** A problem a field's formulas carry, with the field's path. */
  formula(path: string, code: string, message: string): void {
    this.ok = false;
    this.problems.push({ code, op: this.index, message: `${path}: ${message}` });
  }

  str(o: Record<string, unknown>, name: string, at: string, max = 400): string | undefined {
    const value = o[name];
    if (blank(value) || typeof value !== 'string' || value.length > max) return this.bad(`${at}${name}`, `a string of 1 to ${max} characters`);
    return value;
  }

  optStr(o: Record<string, unknown>, name: string, at: string, max = 400): string | undefined {
    const value = o[name];
    if (blank(value)) return undefined;
    if (typeof value !== 'string' || value.length > max) return this.bad(`${at}${name}`, `a string of at most ${max} characters`);
    return value;
  }

  oneOf<T extends string>(o: Record<string, unknown>, name: string, at: string, list: readonly T[]): T | undefined {
    const value = o[name];
    if (typeof value !== 'string' || !(list as readonly string[]).includes(value)) return this.bad(`${at}${name}`, `one of ${list.join(', ')}`);
    return value as T;
  }

  optOneOf<T extends string>(o: Record<string, unknown>, name: string, at: string, list: readonly T[]): T | undefined {
    return blank(o[name]) ? undefined : this.oneOf(o, name, at, list);
  }

  optBool(o: Record<string, unknown>, name: string, at: string): boolean | undefined {
    const value = o[name];
    if (value === undefined || value === null) return undefined;
    if (typeof value !== 'boolean') return this.bad(`${at}${name}`, 'true or false');
    return value;
  }

  optInt(o: Record<string, unknown>, name: string, at: string, min: number, max: number): number | undefined {
    const value = o[name];
    if (value === undefined || value === null) return undefined;
    if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) return this.bad(`${at}${name}`, `a whole number from ${min} to ${max}`);
    return value;
  }

  optNum(o: Record<string, unknown>, name: string, at: string): number | undefined {
    const value = o[name];
    if (value === undefined || value === null) return undefined;
    if (typeof value !== 'number' || !Number.isFinite(value)) return this.bad(`${at}${name}`, 'a number');
    return value;
  }

  list<T>(o: Record<string, unknown>, name: string, at: string, max: number, item: (raw: unknown, at: string) => T | undefined, min = 0): T[] | undefined {
    const value = o[name];
    if (!Array.isArray(value) || value.length < min || value.length > max) return this.bad(`${at}${name}`, `a list of ${min} to ${max}`);
    const out: T[] = [];
    value.forEach((one, position) => {
      const read = item(one, `${at}${name}[${position}].`);
      if (read !== undefined) out.push(read);
    });
    return out;
  }

  optList<T>(o: Record<string, unknown>, name: string, at: string, max: number, item: (raw: unknown, at: string) => T | undefined): T[] | undefined {
    return o[name] === undefined || o[name] === null ? undefined : this.list(o, name, at, max, item);
  }

  object(raw: unknown, at: string): Record<string, unknown> | undefined {
    if (!isObject(raw)) return this.bad(at.replace(/\.$/, ''), 'an object');
    return raw;
  }

  strings(o: Record<string, unknown>, name: string, at: string, max: number): string[] | undefined {
    return this.optList(o, name, at, max, (raw, here) =>
      typeof raw === 'string' && raw.trim() !== '' ? raw : this.bad(here.replace(/\.$/, ''), 'a string'),
    );
  }
}

/** Members of an object, without the ones left absent. */
const defined = <T extends Record<string, unknown>>(object: T): T =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined)) as T;

/* ---------------------------------------------------------- the draft parts */

const readFlatFormula = (raw: unknown, at: string, read: StudioWireReader): FlatFormula | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const result = read.oneOf(o, 'result', at, SETUP_FORMULA_RESULTS);
  const onMissing = read.oneOf(o, 'onMissing', at, ['blank', 'zero'] as const);
  const root = read.str(o, 'root', at, 40);
  const nodes = read.list(o, 'nodes', at, 64, (one, here) => {
    const node = read.object(one, here);
    if (node === undefined) return undefined;
    if (typeof node.id !== 'string' || typeof node.kind !== 'string') return read.bad(here.replace(/\.$/, ''), 'a node with an id and a kind');
    return node as unknown as FlatFormula['nodes'][number];
  }, 1);
  if (result === undefined || onMissing === undefined || root === undefined || nodes === undefined) return undefined;
  return { result, onMissing, root, nodes };
};

const readOption = (raw: unknown, at: string, read: StudioWireReader): NonNullable<SetupDraftField['options']>[number] | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const key = read.str(o, 'key', at, 40);
  const label = read.str(o, 'label', at, 80);
  const sign: 1 | -1 | undefined = o.sign === undefined || o.sign === null ? undefined : o.sign === 1 || o.sign === -1 ? o.sign : read.bad(`${at}sign`, '1 or -1');
  const tone = blank(o.tone) ? undefined : isCaptureTone(o.tone) ? o.tone : read.bad(`${at}tone`, 'a tone');
  if (key === undefined || label === undefined) return undefined;
  return defined({ key, label, sign, tone });
};

/**
 * A field's members as the wire carries them, flat formulas and all, without
 * reading the formulas yet. `partial` reads `update_field`'s `set`: no member
 * is required and `lines` holds only words.
 */
const readFieldMembers = (o: Record<string, unknown>, at: string, read: StudioWireReader, partial: boolean): Record<string, unknown> => {
  const out: Record<string, unknown> = {
    label: partial ? read.optStr(o, 'label', at, 80) : read.str(o, 'label', at, 80),
    type: partial ? read.optOneOf(o, 'type', at, AUTHOR_TYPES) : read.oneOf(o, 'type', at, AUTHOR_TYPES),
    required: read.optBool(o, 'required', at),
    sticky: read.optBool(o, 'sticky', at),
    help: read.optStr(o, 'help', at, SETUP_DRAFT_LIMITS.help),
    options: read.optList(o, 'options', at, SETUP_DRAFT_LIMITS.options, (raw, here) => readOption(raw, here, read)),
    targetRecordKey: read.optStr(o, 'targetRecordKey', at, STUDIO_LIMITS.keyLength),
    maxChosen: read.optInt(o, 'maxChosen', at, 1, 20),
    shape: read.optStr(o, 'shape', at, 40),
    units: read.strings(o, 'units', at, 6),
    quickAmounts: read.strings(o, 'quickAmounts', at, 6),
  };
  if (!blank(o.filter)) {
    const filter = read.object(o.filter, `${at}filter.`);
    if (filter !== undefined) {
      const field = read.str(filter, 'field', `${at}filter.`, STUDIO_LIMITS.keyLength);
      const equals = read.str(filter, 'equals', `${at}filter.`, STUDIO_LIMITS.keyLength);
      if (field !== undefined && equals !== undefined) out.filter = { field, equals };
    }
  }
  for (const name of ['formula', 'startsWith', 'showWhen'] as const) {
    if (!blank(o[name])) out[name] = readFlatFormula(o[name], `${at}${name}.`, read);
  }
  if (!blank(o.lines)) {
    const lines = read.object(o.lines, `${at}lines.`);
    if (lines !== undefined) {
      const here = `${at}lines.`;
      out.lines = partial
        ? defined({
            noun: read.optStr(lines, 'noun', here, 80),
            nouns: read.optStr(lines, 'nouns', here, 80),
            countFrom: read.optStr(lines, 'countFrom', here, STUDIO_LIMITS.keyLength),
            max: read.optInt(lines, 'max', here, 1, 200),
          })
        : defined({
            noun: read.str(lines, 'noun', here, 80),
            nouns: read.str(lines, 'nouns', here, 80),
            countFrom: read.optStr(lines, 'countFrom', here, STUDIO_LIMITS.keyLength),
            max: read.optInt(lines, 'max', here, 1, 200),
            fields: read.list(lines, 'fields', here, 8, (raw, there) => readFieldWire(raw, there, read), 1),
          });
    }
  }
  return defined(out);
};

/** One wire field, not yet with its formulas read: key optional. */
const readFieldWire = (raw: unknown, at: string, read: StudioWireReader): (SetupDraftFieldWire & { key?: string }) | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const key = read.optStr(o, 'key', at, STUDIO_LIMITS.keyLength);
  return defined({ ...readFieldMembers(o, at, read, false), key }) as unknown as SetupDraftFieldWire & { key?: string };
};

/** A wire field with its formulas read into trees, every problem named by the field's path. */
const readField = (raw: unknown, at: string, read: StudioWireReader): StudioFieldInput | undefined => {
  const wire = readFieldWire(raw, at, read);
  if (wire === undefined) return undefined;
  // `readDraftField` names a problem by key; a field still to be minted goes by its label.
  const named = { ...wire, key: wire.key ?? wire.label ?? '?' } as SetupDraftFieldWire;
  const done = readDraftField(withKeys(named), `ops[${read.index}].${at.replace(/\.$/, '')}`);
  for (const problem of done.problems) read.formula(problem.path, problem.code, problem.message);
  return stripKeys(done.field, wire);
};

/** Every line field given a path key for reading, which `stripKeys` takes away again. */
const withKeys = (field: SetupDraftFieldWire): SetupDraftFieldWire =>
  field.lines === undefined
    ? field
    : { ...field, lines: { ...field.lines, fields: field.lines.fields.map((one) => withKeys({ ...one, key: one.key ?? one.label ?? '?' })) } };

const stripKeys = (field: SetupDraftField, wire: { key?: string; lines?: { fields: Array<{ key?: string }> } }): StudioFieldInput => {
  const { key, lines, ...rest } = field;
  return {
    ...rest,
    ...(wire.key === undefined ? {} : { key }),
    ...(lines === undefined
      ? {}
      : { lines: { ...lines, fields: lines.fields.map((one, at) => stripKeys(one, wire.lines?.fields[at] ?? {})) } }),
  } as StudioFieldInput;
};

const readTile = (raw: unknown, at: string, read: StudioWireReader): StudioTileInput | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const card = isInsightCardId(o.card) ? o.card : read.bad(`${at}card`, 'a card id');
  const tile = {
    key: read.optStr(o, 'key', at, STUDIO_LIMITS.keyLength),
    label: read.str(o, 'label', at, 80),
    card,
    agg: read.oneOf(o, 'agg', at, SETUP_AGGS),
    fieldKey: read.optStr(o, 'fieldKey', at, STUDIO_LIMITS.keyLength),
    scope: read.optOneOf(o, 'scope', at, SETUP_TILE_SCOPES),
    groupByFieldKey: read.optStr(o, 'groupByFieldKey', at, STUDIO_LIMITS.keyLength),
    days: read.optInt(o, 'days', at, 1, SETUP_DRAFT_LIMITS.tileDays),
    limit: read.optInt(o, 'limit', at, 1, SETUP_DRAFT_LIMITS.tileGroups),
    where: read.optList(o, 'where', at, SETUP_DRAFT_LIMITS.tileWhere, (one, here) => {
      const where = read.object(one, here);
      if (where === undefined) return undefined;
      const fieldKey = read.str(where, 'fieldKey', here, STUDIO_LIMITS.keyLength);
      const op = read.oneOf(where, 'op', here, SETUP_TILE_OPS);
      if (fieldKey === undefined || op === undefined) return undefined;
      return defined({ fieldKey, op, optionKey: read.optStr(where, 'optionKey', here, STUDIO_LIMITS.keyLength), num: read.optNum(where, 'num', here) });
    }),
  };
  if (tile.label === undefined || card === undefined || tile.agg === undefined) return undefined;
  return defined(tile) as StudioTileInput;
};

const readCapture = (raw: unknown, at: string, read: StudioWireReader): SetupDraftRecord['capture'] | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const mode = read.oneOf(o, 'mode', at, ['sheet'] as const);
  const overFieldKey = read.str(o, 'overFieldKey', at, STUDIO_LIMITS.keyLength);
  const cellFieldKey = read.str(o, 'cellFieldKey', at, STUDIO_LIMITS.keyLength);
  if (mode === undefined || overFieldKey === undefined || cellFieldKey === undefined) return undefined;
  return defined({
    mode,
    overFieldKey,
    cellFieldKey,
    sharedFieldKeys: read.strings(o, 'sharedFieldKeys', at, SETUP_DRAFT_LIMITS.sheetSharedFields),
    quickOptionKey: read.optStr(o, 'quickOptionKey', at, STUDIO_LIMITS.keyLength),
  });
};

const readSection = (raw: unknown, at: string, read: StudioWireReader, inner: boolean): SetupDraftSection | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const style = read.oneOf(o, 'style', at, ['plain', 'card', 'folded'] as const);
  const fieldKeys = read.list(o, 'fieldKeys', at, SETUP_DRAFT_LIMITS.sectionFields, (one, here) =>
    typeof one === 'string' && one !== '' ? one : read.bad(here.replace(/\.$/, ''), 'a field key'),
  );
  const sections = inner ? undefined : read.optList(o, 'sections', at, SETUP_DRAFT_LIMITS.innerSections, (one, here) => readSection(one, here, read, true));
  if (style === undefined || fieldKeys === undefined) return undefined;
  return defined({ title: read.optStr(o, 'title', at, SETUP_DRAFT_LIMITS.help), style, fieldKeys, sections }) as SetupDraftSection;
};

const readSeedRow = (raw: unknown, at: string, read: StudioWireReader): SetupDraftSeedRow | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const values = read.list(o, 'values', at, STUDIO_LIMITS.fields, (one, here) => {
    const cell = read.object(one, here);
    if (cell === undefined) return undefined;
    const fieldKey = read.str(cell, 'fieldKey', here, STUDIO_LIMITS.keyLength);
    const value = typeof cell.value === 'string' || (typeof cell.value === 'number' && Number.isFinite(cell.value)) ? cell.value : read.bad(`${here}value`, 'a string or a number');
    return fieldKey === undefined || value === undefined ? undefined : { fieldKey, value };
  });
  return values === undefined ? undefined : { values };
};

export const readStudioFrom = (raw: unknown, at: string, read: StudioWireReader): StudioFrom | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const kind = read.oneOf(o, 'kind', at, ['said', 'source', 'looked_up', 'os'] as const);
  const quote = read.optStr(o, 'quote', at, STUDIO_LIMITS.quote);
  switch (kind) {
    case 'said':
      return defined({ kind, quote });
    case 'source': {
      const source = read.str(o, 'source', at, 40);
      return source === undefined ? undefined : defined({ kind, source, part: read.optStr(o, 'part', at, 120), quote, finding: read.optStr(o, 'finding', at, 40) });
    }
    case 'looked_up': {
      const topic = read.str(o, 'topic', at, 120);
      return topic === undefined ? undefined : { kind, topic };
    }
    case 'os':
      return { kind };
    default:
      return undefined;
  }
};

export const readFromList = (o: Record<string, unknown>, read: StudioWireReader): StudioFrom[] | undefined =>
  read.optList(o, 'from', '', STUDIO_LIMITS.fromPerPart, (raw, at) => readStudioFrom(raw, at, read));

const readWithin = (o: Record<string, unknown>, read: StudioWireReader): string[] | undefined => {
  const within = read.strings(o, 'within', '', 3);
  return within === undefined || within.length === 0 ? undefined : within;
};

export const readPartRef = (raw: unknown, at: string, read: StudioWireReader): StudioPartRef | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const kind = read.oneOf(o, 'kind', at, ['workspace', 'record', 'field', 'card', 'link', 'board', 'role', 'import', 'note', 'not_yet', 'source'] as const);
  switch (kind) {
    case 'workspace':
      return { kind };
    case 'record': {
      const record = read.str(o, 'record', at, STUDIO_LIMITS.keyLength);
      return record === undefined ? undefined : { kind, record };
    }
    case 'field': {
      const record = read.str(o, 'record', at, STUDIO_LIMITS.keyLength);
      const field = read.str(o, 'field', at, STUDIO_LIMITS.keyLength);
      const within = readWithin(o, read);
      return record === undefined || field === undefined ? undefined : defined({ kind, record, within, field });
    }
    case 'card': {
      const record = read.str(o, 'record', at, STUDIO_LIMITS.keyLength);
      const card = read.str(o, 'card', at, STUDIO_LIMITS.keyLength);
      return record === undefined || card === undefined ? undefined : { kind, record, card };
    }
    case 'source': {
      const id = read.str(o, 'id', at, 40);
      return id === undefined ? undefined : { kind, id };
    }
    case undefined:
      return undefined;
    default: {
      const key = read.str(o, 'key', at, STUDIO_LIMITS.keyLength);
      return key === undefined ? undefined : { kind, key };
    }
  }
};

const cascadeOf = (o: Record<string, unknown>, read: StudioWireReader) => read.optOneOf(o, 'cascade', '', ['refuse', 'with_dependents'] as const);

const recordProps = (o: Record<string, unknown>, read: StudioWireReader) => ({
  entryNoun: read.optStr(o, 'entryNoun', '', 80),
  direction: read.optOneOf(o, 'direction', '', SETUP_DIRECTIONS),
  icon: blank(o.icon) ? undefined : isRecordIconId(o.icon) ? (o.icon as RecordIconId) : read.bad('icon', 'an icon id'),
  headlineFieldKey: read.optStr(o, 'headlineFieldKey', '', STUDIO_LIMITS.keyLength),
  dateFieldKey: read.optStr(o, 'dateFieldKey', '', STUDIO_LIMITS.keyLength),
  measureFieldKey: read.optStr(o, 'measureFieldKey', '', STUDIO_LIMITS.keyLength),
  signFieldKey: read.optStr(o, 'signFieldKey', '', STUDIO_LIMITS.keyLength),
  focusFieldKey: read.optStr(o, 'focusFieldKey', '', STUDIO_LIMITS.keyLength),
  capture: blank(o.capture) ? undefined : readCapture(o.capture, 'capture.', read),
  layout: read.optList(o, 'layout', '', SETUP_DRAFT_LIMITS.sections, (raw, at) => readSection(raw, at, read, false)),
});

/** The formulas of `update_field`'s `set`, read into trees. */
const readFieldSet = (raw: unknown, read: StudioWireReader): StudioFieldSet | undefined => {
  const o = read.object(raw, 'set.');
  if (o === undefined) return undefined;
  const members = readFieldMembers(o, 'set.', read, true);
  const out: Record<string, unknown> = { ...members };
  for (const name of ['formula', 'startsWith', 'showWhen'] as const) {
    const flat = members[name] as FlatFormula | undefined;
    if (flat === undefined) continue;
    const done = readDraftField({ key: 'set', label: 'set', type: 'formula', [name]: flat } as SetupDraftFieldWire, `ops[${read.index}]`);
    for (const problem of done.problems) read.formula(problem.path, problem.code, problem.message);
    out[name] = (done.field as Record<string, unknown>)[name] as SetupDraftFormula | undefined;
  }
  return defined(out) as StudioFieldSet;
};

/* ----------------------------------------------------------- the op readers */

/** Every op's reader, by name. 2.5 registers its own beside these. */
export const STUDIO_OP_READERS: Partial<Record<string, StudioOpReader>> = {
  set_workspace: (o, read) =>
    defined({
      op: 'set_workspace' as const,
      name: read.optStr(o, 'name', '', 200),
      currency: read.optStr(o, 'currency', '', 3),
      language: read.optOneOf(o, 'language', '', AGENT_LANGUAGE_TAGS) as AgentLanguageTag | undefined,
      place: read.optStr(o, 'place', '', 200),
      does: read.optStr(o, 'does', '', 400),
      site: read.optStr(o, 'site', '', 400),
      clear: read.optList(o, 'clear', '', 3, (raw, at) => (raw === 'place' || raw === 'does' || raw === 'site' ? raw : read.bad(at.replace(/\.$/, ''), 'place, does or site'))),
    }),
  add_record: (o, read) => {
    const op = defined({
      op: 'add_record' as const,
      key: read.optStr(o, 'key', '', STUDIO_LIMITS.keyLength),
      name: read.str(o, 'name', '', 80),
      kind: read.oneOf(o, 'kind', '', ['record', 'directory'] as const),
      ...recordProps(o, read),
      fields: read.list(o, 'fields', '', STUDIO_LIMITS.fields, (raw, at) => readField(raw, at, read), 1),
      seedRows: read.optList(o, 'seedRows', '', STUDIO_LIMITS.seedRows, (raw, at) => readSeedRow(raw, at, read)),
      tiles: read.optList(o, 'tiles', '', STUDIO_LIMITS.tilesPerRecord, (raw, at) => readTile(raw, at, read)),
      before: read.optStr(o, 'before', '', STUDIO_LIMITS.keyLength),
      from: readFromList(o, read),
    });
    return op.name === undefined || op.kind === undefined || op.fields === undefined ? null : (op as StudioOp);
  },
  update_record: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    const clear = read.optList(o, 'clear', '', STUDIO_RECORD_CLEARABLE.length, (raw, at) =>
      (STUDIO_RECORD_CLEARABLE as readonly unknown[]).includes(raw) ? (raw as (typeof STUDIO_RECORD_CLEARABLE)[number]) : read.bad(at.replace(/\.$/, ''), STUDIO_RECORD_CLEARABLE.join(', ')),
    );
    return record === undefined ? null : (defined({ op: 'update_record' as const, record, ...recordProps(o, read), clear }) as StudioOp);
  },
  rename: (o, read) => {
    const target = readPartRef(o.target, 'target.', read);
    const name = read.str(o, 'name', '', 80);
    return target === undefined || name === undefined ? null : { op: 'rename', target, name };
  },
  remove_record: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    return record === undefined ? null : (defined({ op: 'remove_record' as const, record, cascade: cascadeOf(o, read) }) as StudioOp);
  },
  add_field: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    const field = readField(o.field, 'field.', read);
    if (record === undefined || field === undefined) return null;
    return defined({
      op: 'add_field' as const,
      record,
      within: readWithin(o, read),
      field,
      before: read.optStr(o, 'before', '', STUDIO_LIMITS.keyLength),
      from: readFromList(o, read),
    }) as StudioOp;
  },
  update_field: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    const field = read.str(o, 'field', '', STUDIO_LIMITS.keyLength);
    const set = o.set === undefined ? {} : readFieldSet(o.set, read);
    const clear = read.optList(o, 'clear', '', STUDIO_FIELD_CLEARABLE.length, (raw, at) =>
      (STUDIO_FIELD_CLEARABLE as readonly unknown[]).includes(raw) ? (raw as (typeof STUDIO_FIELD_CLEARABLE)[number]) : read.bad(at.replace(/\.$/, ''), STUDIO_FIELD_CLEARABLE.join(', ')),
    );
    if (record === undefined || field === undefined || set === undefined) return null;
    return defined({ op: 'update_field' as const, record, within: readWithin(o, read), field, set, clear }) as StudioOp;
  },
  remove_field: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    const field = read.str(o, 'field', '', STUDIO_LIMITS.keyLength);
    if (record === undefined || field === undefined) return null;
    return defined({ op: 'remove_field' as const, record, within: readWithin(o, read), field, cascade: cascadeOf(o, read) }) as StudioOp;
  },
  move_field: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    const field = read.str(o, 'field', '', STUDIO_LIMITS.keyLength);
    if (record === undefined || field === undefined) return null;
    return defined({
      op: 'move_field' as const,
      record,
      within: readWithin(o, read),
      field,
      before: read.optStr(o, 'before', '', STUDIO_LIMITS.keyLength),
    }) as StudioOp;
  },
  seed_list: (o, read) => {
    const record = read.str(o, 'record', '', STUDIO_LIMITS.keyLength);
    const rows = read.list(o, 'rows', '', STUDIO_LIMITS.seedRows, (raw, at) => readSeedRow(raw, at, read));
    const mode = read.oneOf(o, 'mode', '', ['replace', 'append'] as const);
    return record === undefined || rows === undefined || mode === undefined ? null : { op: 'seed_list', record, rows, mode };
  },
};

/* ------------------------------------- links, cards, roles, imports, notes */

const KEY = STUDIO_LIMITS.keyLength;

const readCondition = (raw: unknown, at: string, read: StudioWireReader): StudioLinkCondition | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const field = read.str(o, 'field', at, KEY);
  const op = read.oneOf(o, 'op', at, LINK_CONDITION_OPS);
  const values =
    o.values === undefined || o.values === null
      ? []
      : read.list(o, 'values', at, INSIGHT_WHERE_MAX_VALUES, (one, here) =>
          typeof one === 'string' || (typeof one === 'number' && Number.isFinite(one)) ? one : read.bad(here.replace(/\.$/, ''), 'a string or a number'),
        );
  if (field === undefined || op === undefined || values === undefined) return undefined;
  return defined({ field, op, values, via: read.optStr(o, 'via', at, KEY) });
};

const readFill = (raw: unknown, at: string, read: StudioWireReader, linkKey: string): StudioLinkFill | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const to = read.str(o, 'to', at, KEY);
  const kind = read.oneOf(o, 'kind', at, ['field', 'const', 'reference', 'formula'] as const);
  if (to === undefined || kind === undefined) return undefined;
  switch (kind) {
    case 'field': {
      const field = read.str(o, 'field', at, KEY);
      return field === undefined ? undefined : { to, kind, field };
    }
    case 'const': {
      const value = o.value;
      if (typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) return { to, kind, value };
      return read.bad(`${at}value`, 'a string, a number or true or false');
    }
    case 'reference': {
      const via = read.str(o, 'via', at, KEY);
      const field = read.str(o, 'field', at, KEY);
      return via === undefined || field === undefined ? undefined : { to, kind, via, field };
    }
    case 'formula': {
      const flat = readFlatFormula(o.formula, `${at}formula.`, read);
      if (flat === undefined) return undefined;
      const done = readDraftFormula(flat, `links[${linkKey}].fills[${to}]`);
      if (!done.ok) {
        for (const problem of done.problems) read.formula(problem.path, problem.code, problem.message);
        return undefined;
      }
      return { to, kind, formula: done.value };
    }
  }
};

const readWhen = (o: Record<string, unknown>, at: string, read: StudioWireReader): StudioLink['when'] | undefined =>
  read.list(o, 'when', at, 8, (raw, here) => {
    const group = read.object(raw, here);
    if (group === undefined) return undefined;
    const any = read.list(group, 'any', here, 8, (one, there) => readCondition(one, there, read));
    return any === undefined ? undefined : { any };
  });

const readFills = (o: Record<string, unknown>, at: string, read: StudioWireReader, linkKey: string): StudioLinkFill[] | undefined =>
  read.list(o, 'fills', at, STUDIO_LIMITS.fields, (raw, here) => readFill(raw, here, read, linkKey));

const readLink = (raw: unknown, read: StudioWireReader): (Omit<StudioLink, 'key'> & { key?: string }) | undefined => {
  const o = read.object(raw, 'link.');
  if (o === undefined) return undefined;
  const at = 'link.';
  const key = read.optStr(o, 'key', at, KEY);
  const from = read.str(o, 'from', at, KEY);
  const to = read.str(o, 'to', at, KEY);
  const pattern = read.oneOf(o, 'pattern', at, ['feed'] as const);
  const sign: 1 | -1 | undefined = o.sign === 1 || o.sign === -1 ? o.sign : read.bad(`${at}sign`, '1 or -1');
  const mode = read.optOneOf(o, 'mode', at, LINK_MODES) ?? 'auto';
  const when = o.when === undefined ? [] : readWhen(o, at, read);
  const fills = readFills(o, at, read, key ?? `${from}_to_${to}`);
  if (from === undefined || to === undefined || pattern === undefined || sign === undefined || when === undefined || fills === undefined) return undefined;
  return defined({ key, from, to, pattern, sign, mode, when, fills });
};

const readCardNode = (raw: unknown, at: string, read: StudioWireReader): StudioCardNodeWire | undefined => {
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const id = read.str(o, 'id', at, 40);
  const kind = read.oneOf(o, 'kind', at, ['agg', 'const', 'calc', 'round'] as const);
  if (id === undefined || kind === undefined) return undefined;
  switch (kind) {
    case 'const': {
      const value = read.optNum(o, 'value', at);
      return value === undefined ? read.bad(`${at}value`, 'a number') : { id, kind, value };
    }
    case 'calc': {
      const op = read.oneOf(o, 'op', at, SETUP_FORMULA_CALC_OPS);
      const left = read.str(o, 'left', at, 40);
      const right = read.str(o, 'right', at, 40);
      return op === undefined || left === undefined || right === undefined ? undefined : { id, kind, op, left, right };
    }
    case 'round': {
      const of = read.str(o, 'of', at, 40);
      const decimals = read.optInt(o, 'decimals', at, 0, 8) ?? 0;
      return of === undefined ? undefined : { id, kind, of, decimals };
    }
    case 'agg': {
      const agg = read.oneOf(o, 'agg', at, ['sum', 'avg', 'min', 'max', 'count', 'rows'] as const);
      const record = read.str(o, 'record', at, KEY);
      const where = read.optList(o, 'where', at, 8, (one, here) => {
        const w = read.object(one, here);
        if (w === undefined) return undefined;
        const field = read.str(w, 'field', here, KEY);
        const op = read.oneOf(w, 'op', here, INSIGHT_WHERE_OPS);
        const value = w.value;
        const okValue =
          value === undefined ||
          value === null ||
          typeof value === 'string' ||
          (typeof value === 'number' && Number.isFinite(value)) ||
          (Array.isArray(value) && value.length <= INSIGHT_WHERE_MAX_VALUES && value.every((one) => typeof one === 'string' || typeof one === 'number'));
        if (!okValue) return read.bad(`${here}value`, 'a string, a number or a list of them');
        if (field === undefined || op === undefined) return undefined;
        return defined({ field, op, value: value ?? undefined, via: read.optStr(w, 'via', here, KEY) }) as StudioCardWhere;
      });
      if (agg === undefined || record === undefined) return undefined;
      return defined({
        id,
        kind,
        agg,
        record,
        ofKind: read.optOneOf(o, 'ofKind', at, ['measure', 'field', 'expr'] as const),
        ofField: read.optStr(o, 'ofField', at, KEY),
        ofFormula: blank(o.ofFormula) ? undefined : readFlatFormula(o.ofFormula, `${at}ofFormula.`, read),
        where,
        groupBy: read.optStr(o, 'groupBy', at, KEY),
      });
    }
  }
};

const readBoardCard = (raw: unknown, read: StudioWireReader): (Omit<StudioBoardCard, 'key'> & { key?: string }) | undefined => {
  const at = 'card.';
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const card = isInsightCardId(o.card) ? o.card : read.bad(`${at}card`, 'a card id');
  const wire = {
    key: read.optStr(o, 'key', at, KEY),
    label: read.str(o, 'label', at, 80),
    card,
    scope: read.optOneOf(o, 'scope', at, SETUP_TILE_SCOPES),
    days: read.optInt(o, 'days', at, 1, 400),
    limit: read.optInt(o, 'limit', at, 1, 50),
    root: read.str(o, 'root', at, 40),
    nodes: read.list(o, 'nodes', at, STUDIO_CARD_WIRE_NODES, (one, here) => readCardNode(one, here, read), 1),
  };
  if (wire.label === undefined || card === undefined || wire.root === undefined || wire.nodes === undefined || !read.ok) return undefined;
  const done = readBoardCardWire(defined(wire) as StudioBoardCardWire);
  if (!done.ok) {
    for (const problem of done.problems) read.formula(`ops[${read.index}].card`, problem.code, problem.message);
    return undefined;
  }
  return done.card;
};

const readPermissions = (o: Record<string, unknown>, at: string, read: StudioWireReader): StudioRole['permissions'] | undefined =>
  read.list(o, 'permissions', at, 64, (raw, here) => {
    const pair = read.object(raw, here);
    if (pair === undefined) return undefined;
    const key = read.str(pair, 'key', here, 40);
    const value = pair.value;
    if (typeof value !== 'boolean' && typeof value !== 'string') return read.bad(`${here}value`, 'a reach or true or false');
    return key === undefined ? undefined : ({ key, value } as StudioRole['permissions'][number]);
  });

const readAccess = (o: Record<string, unknown>, at: string, read: StudioWireReader): StudioRole['access'] | undefined =>
  read.list(o, 'access', at, STUDIO_LIMITS.records, (raw, here) => {
    const row = read.object(raw, here);
    if (row === undefined) return undefined;
    const record = read.str(row, 'record', here, KEY);
    const level = read.oneOf(row, 'level', here, ACCESS_LEVELS);
    return record === undefined || level === undefined ? undefined : { record, level };
  });

const readPeople = (o: Record<string, unknown>, at: string, read: StudioWireReader): string[] | undefined =>
  read.list(o, 'people', at, STUDIO_LIMITS.rolePeople, (raw, here) =>
    typeof raw === 'string' && raw.trim() !== '' ? raw : read.bad(here.replace(/\.$/, ''), 'a name'),
  );

const readRole = (raw: unknown, read: StudioWireReader): (Omit<StudioRole, 'key'> & { key?: string }) | undefined => {
  const at = 'role.';
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const role = {
    key: read.optStr(o, 'key', at, KEY),
    name: read.str(o, 'name', at, 80),
    purpose: read.optStr(o, 'purpose', at, 600),
    people: o.people === undefined ? [] : readPeople(o, at, read),
    base: read.oneOf(o, 'base', at, STUDIO_ROLE_BASES),
    permissions: o.permissions === undefined ? [] : readPermissions(o, at, read),
    access: o.access === undefined ? [] : readAccess(o, at, read),
  };
  if (role.name === undefined || role.people === undefined || role.base === undefined || role.permissions === undefined || role.access === undefined) return undefined;
  return defined(role) as Omit<StudioRole, 'key'> & { key?: string };
};

const readImport = (raw: unknown, read: StudioWireReader): (Omit<StudioImport, 'key'> & { key?: string }) | undefined => {
  const at = 'import.';
  const o = read.object(raw, at);
  if (o === undefined) return undefined;
  const headerRow = o.headerRow === null ? null : read.optInt(o, 'headerRow', at, 0, 10_000);
  const columns = read.list(o, 'columns', at, STUDIO_LIMITS.importColumns, (one, here) => {
    const column = read.object(one, here);
    if (column === undefined) return undefined;
    const index = column.column === null ? null : read.optInt(column, 'column', here, 0, 10_000);
    const field = blank(column.field) ? null : read.str(column, 'field', here, KEY);
    const transform = isColumnTransform(column.transform) ? column.transform : read.bad(`${here}transform`, 'a way a column is read');
    if (index === undefined || field === undefined || transform === undefined) return undefined;
    return { column: index, header: read.optStr(column, 'header', here, 120) ?? '', field, transform };
  }, 1);
  const plan = {
    key: read.optStr(o, 'key', at, KEY),
    source: read.str(o, 'source', at, 40),
    sheet: read.str(o, 'sheet', at, 120),
    record: read.str(o, 'record', at, KEY),
    headerRow: headerRow === undefined ? null : headerRow,
    skipRows: o.skipRows === undefined ? [] : read.list(o, 'skipRows', at, 200, (one, here) => (typeof one === 'number' && Number.isInteger(one) && one >= 0 ? one : read.bad(here.replace(/\.$/, ''), 'a row number'))),
    columns,
    rows: read.optInt(o, 'rows', at, 0, 10_000_000),
    unfilled:
      o.unfilled === undefined
        ? undefined
        : read.list(o, 'unfilled', at, STUDIO_LIMITS.fields, (one, here) => (typeof one === 'string' && STUDIO_KEY.test(one) ? one : read.bad(here.replace(/\.$/, ''), 'a field key'))),
  };
  if (plan.source === undefined || plan.sheet === undefined || plan.record === undefined || plan.skipRows === undefined || plan.columns === undefined) return undefined;
  return defined(plan) as Omit<StudioImport, 'key'> & { key?: string };
};

const keyOf = (name: string) => (o: Record<string, unknown>, read: StudioWireReader) => read.str(o, name, '', KEY);

/** An op that names one part by key: `{ op, <member>: key }`. */
const byKey = <K extends StudioOp['op']>(op: K, member: string): StudioOpReader => (o, read) => {
  const key = keyOf(member)(o, read);
  return key === undefined ? null : ({ op, [member]: key } as unknown as StudioOp);
};

Object.assign(STUDIO_OP_READERS, {
  add_link: (o, read) => {
    const link = readLink(o.link, read);
    return link === undefined ? null : (defined({ op: 'add_link' as const, link, from: readFromList(o, read) }) as StudioOp);
  },
  update_link: (o, read) => {
    const link = keyOf('link')(o, read);
    const op = defined({
      op: 'update_link' as const,
      link,
      sign: o.sign === undefined || o.sign === null ? undefined : o.sign === 1 || o.sign === -1 ? (o.sign as 1 | -1) : read.bad('sign', '1 or -1'),
      mode: read.optOneOf(o, 'mode', '', LINK_MODES),
      when: o.when === undefined ? undefined : readWhen(o, '', read),
      fills: o.fills === undefined ? undefined : readFills(o, '', read, link ?? '?'),
    });
    return link === undefined ? null : (op as StudioOp);
  },
  remove_link: byKey('remove_link', 'link'),
  add_card: (o, read) => {
    const record = keyOf('record')(o, read);
    const card = readTile(o.card, 'card.', read);
    return record === undefined || card === undefined ? null : (defined({ op: 'add_card' as const, record, card, from: readFromList(o, read) }) as StudioOp);
  },
  remove_card: (o, read) => {
    const record = keyOf('record')(o, read);
    const card = keyOf('card')(o, read);
    return record === undefined || card === undefined ? null : { op: 'remove_card', record, card };
  },
  add_board_card: (o, read) => {
    const card = readBoardCard(o.card, read);
    return card === undefined ? null : (defined({ op: 'add_board_card' as const, card, from: readFromList(o, read) }) as StudioOp);
  },
  remove_board_card: byKey('remove_board_card', 'card'),
  add_role: (o, read) => {
    const role = readRole(o.role, read);
    return role === undefined ? null : (defined({ op: 'add_role' as const, role, from: readFromList(o, read) }) as StudioOp);
  },
  update_role: (o, read) => {
    const role = keyOf('role')(o, read);
    const op = defined({
      op: 'update_role' as const,
      role,
      purpose: read.optStr(o, 'purpose', '', 600),
      people: o.people === undefined ? undefined : readPeople(o, '', read),
      base: read.optOneOf(o, 'base', '', STUDIO_ROLE_BASES),
      permissions: o.permissions === undefined ? undefined : readPermissions(o, '', read),
      access: o.access === undefined ? undefined : readAccess(o, '', read),
      clear: read.optList(o, 'clear', '', 1, (raw, at) => (raw === 'purpose' ? ('purpose' as const) : read.bad(at.replace(/\.$/, ''), 'purpose'))),
    });
    return role === undefined ? null : (op as StudioOp);
  },
  remove_role: byKey('remove_role', 'role'),
  map_import: (o, read) => {
    const plan = readImport(o.import, read);
    return plan === undefined ? null : { op: 'map_import', import: plan };
  },
  remove_import: byKey('remove_import', 'import'),
  set_source: (o, read) => {
    const source = read.str(o, 'source', '', 40);
    const role = read.oneOf(o, 'role', '', STUDIO_SOURCE_ROLES);
    return source === undefined || role === undefined ? null : (defined({ op: 'set_source' as const, source, role, dismissed: read.optBool(o, 'dismissed', '') }) as StudioOp);
  },
  set_from: (o, read) => {
    const path = read.str(o, 'path', '', 200);
    const from = read.list(o, 'from', '', STUDIO_LIMITS.fromPerPart, (raw, at) => readStudioFrom(raw, at, read));
    return path === undefined || from === undefined ? null : { op: 'set_from', path, from };
  },
  note: (o, read) => {
    const n = read.object(o.note, 'note.');
    if (n === undefined) return null;
    const note = {
      key: read.optStr(n, 'key', 'note.', KEY),
      about: read.str(n, 'about', 'note.', 200),
      kind: read.oneOf(n, 'kind', 'note.', STUDIO_NOTE_KINDS),
      text: read.str(n, 'text', 'note.', STUDIO_LIMITS.noteText),
      sources: n.sources === undefined ? [] : read.list(n, 'sources', 'note.', STUDIO_LIMITS.sources, (raw, at) => (typeof raw === 'string' && raw !== '' ? raw : read.bad(at.replace(/\.$/, ''), 'a source id'))),
      card: read.optStr(n, 'card', 'note.', KEY),
    };
    if (note.about === undefined || note.kind === undefined || note.text === undefined || note.sources === undefined) return null;
    return { op: 'note', note: defined(note) as Omit<StudioNote, 'key'> & { key?: string } };
  },
  remove_note: byKey('remove_note', 'note'),
  update_note: (o, read) => {
    const note = read.str(o, 'note', '', KEY);
    const op = defined({
      op: 'update_note' as const,
      note,
      card: read.optStr(o, 'card', '', KEY),
      sources: read.optList(o, 'sources', '', STUDIO_LIMITS.sources, (raw, at) => (typeof raw === 'string' && raw.length <= 40 ? raw : read.bad(at.replace(/\.$/, ''), 'a source id'))),
      clear: read.optList(o, 'clear', '', 1, (raw, at) => (raw === 'card' ? ('card' as const) : read.bad(at.replace(/\.$/, ''), 'card'))),
    });
    return note === undefined ? null : (op as StudioOp);
  },
  not_yet: (o, read) => {
    const n = read.object(o.item, 'item.');
    if (n === undefined) return null;
    const at = 'item.';
    const item = {
      key: read.optStr(n, 'key', at, KEY),
      kind: read.oneOf(n, 'kind', at, STUDIO_NOT_YET_KINDS),
      name: read.str(n, 'name', at, 80),
      fit: read.str(n, 'fit', at, 200),
      level: read.oneOf(n, 'level', at, UNMET_NEED_LEVELS),
      archetype: read.str(n, 'archetype', at, 60),
      need: read.str(n, 'need', at, 280),
      capability: read.optOneOf(n, 'capability', at, CAPABILITY_IDS),
    };
    if (Object.entries(item).some(([name, value]) => name !== 'key' && name !== 'capability' && value === undefined)) return null;
    return { op: 'not_yet', item: defined(item) as Omit<StudioNotYet, 'key'> & { key?: string } };
  },
  remove_not_yet: byKey('remove_not_yet', 'item'),
  dismiss_gap: (o, read) => {
    const gap = read.str(o, 'gap', '', 80);
    return gap === undefined ? null : (defined({ op: 'dismiss_gap' as const, gap, reason: read.optStr(o, 'reason', '', STUDIO_LIMITS.reason) }) as StudioOp);
  },
  add_check: (o, read) => {
    const c = read.object(o.check, 'check.');
    if (c === undefined) return null;
    const at = 'check.';
    const on = c.on === null || c.on === undefined ? null : readPartRef(c.on, `${at}on.`, read);
    const check = {
      id: read.str(c, 'id', at, 80),
      title: read.str(c, 'title', at, 120),
      question: read.str(c, 'question', at, 200),
      why: read.str(c, 'why', at, 160),
      chips: read.list(c, 'chips', at, 2, (raw, here) => {
        const chip = read.object(raw, here);
        const label = chip === undefined ? undefined : read.str(chip, 'label', here, 40);
        if (chip === undefined || label === undefined) return undefined;
        return read.optBool(chip, 'keeps', here) === true ? { label, keeps: true as const } : { label };
      }),
    };
    if (on === undefined || Object.values(check).some((value) => value === undefined)) return null;
    return { op: 'add_check', check: { ...(check as Omit<CriticCheck, 'on'>), on } };
  },
  drop_check: (o, read) => {
    const id = read.str(o, 'id', '', 80);
    return id === undefined ? null : { op: 'drop_check', id };
  },
  reopen_gap: (o, read) => {
    const gap = read.str(o, 'gap', '', 80);
    return gap === undefined ? null : { op: 'reopen_gap', gap };
  },
  pick_conflict: (o, read) => {
    const conflictId = read.str(o, 'conflictId', '', 120);
    const sourceId = read.str(o, 'sourceId', '', 80);
    return conflictId === undefined || sourceId === undefined ? null : { op: 'pick_conflict', conflictId, sourceId };
  },
  later_conflict: (o, read) => {
    const conflictId = read.str(o, 'conflictId', '', 120);
    return conflictId === undefined ? null : { op: 'later_conflict', conflictId };
  },
  set_app_map: (o, read) => {
    const m = read.object(o.map, 'map.');
    if (m === undefined) return null;
    const at = 'map.';
    const rows = read.list(m, 'rows', at, STUDIO_LIMITS.appMapRows * 2, (raw, here) => {
      const r = read.object(raw, here);
      if (r === undefined) return undefined;
      const theirs = typeof r.theirs === 'string' && r.theirs.length <= 120 ? r.theirs : read.bad(`${here}theirs`, 'their words, up to 120 characters');
      const part = r.part === null ? null : read.str(r, 'part', here, 200);
      const kind = r.kind === null || r.kind === undefined ? null : read.oneOf(r, 'kind', here, STUDIO_APP_MAP_KINDS);
      const status = read.oneOf(r, 'status', here, STUDIO_APP_MAP_STATUSES);
      if (theirs === undefined || part === undefined || kind === undefined || status === undefined) return undefined;
      return defined({ theirs, part, kind, status, note: read.optStr(r, 'note', here, 160), notYet: read.optStr(r, 'notYet', here, KEY) }) as StudioAppMap['rows'][number];
    });
    const map = {
      id: read.str(m, 'id', at, KEY),
      source: read.str(m, 'source', at, 120),
      intent: read.oneOf(m, 'intent', at, STUDIO_APP_MAP_INTENTS),
      pages: read.optInt(m, 'pages', at, 0, 1000) ?? 0,
      rows,
      notPicked: read.list(m, 'notPicked', at, 40, (raw, here) => (typeof raw === 'string' && raw.length <= 80 ? raw : read.bad(here.replace(/\.$/, ''), 'a name'))),
    };
    if (Object.values(map).some((value) => value === undefined)) return null;
    return { op: 'set_app_map', map: map as StudioAppMap };
  },
  remove_app_map: (o, read) => {
    const id = read.str(o, 'id', '', KEY);
    return id === undefined ? null : { op: 'remove_app_map', id };
  },
} satisfies Partial<Record<StudioOp['op'], StudioOpReader>>);

/** Every op name the wire reads. */
export const studioOpNames = (): string[] => Object.keys(STUDIO_OP_READERS);

/** A batch off the wire, read into strict ops, or every problem with it at once. */
export const readStudioOps = (raw: unknown): { ok: true; ops: StudioOp[] } | { ok: false; problems: StudioOpProblem[] } => {
  if (!Array.isArray(raw)) return { ok: false, problems: [{ code: 'OPS_NOT_LIST', op: null, message: 'ops must be a list.' }] };
  if (raw.length > STUDIO_LIMITS.opsPerBatch) {
    return { ok: false, problems: [{ code: 'OPS_TOO_MANY', op: null, message: `A batch holds ${STUDIO_LIMITS.opsPerBatch} ops at most; this one holds ${raw.length}.` }] };
  }
  const problems: StudioOpProblem[] = [];
  const ops: StudioOp[] = [];
  raw.forEach((one, index) => {
    if (!isObject(one)) {
      problems.push({ code: 'OP_SHAPE', op: index, message: `ops[${index}] must be an object with an op.` });
      return;
    }
    // Own names only: a name every object inherits (`constructor`, `__proto__`) is not an op.
    const reader = typeof one.op === 'string' && Object.hasOwn(STUDIO_OP_READERS, one.op) ? STUDIO_OP_READERS[one.op] : undefined;
    if (reader === undefined) {
      problems.push({ code: 'OP_UNKNOWN', op: index, message: `ops[${index}].op "${String(one.op)}" is not an op. The ops are: ${studioOpNames().join(', ')}.` });
      return;
    }
    const read = new StudioWireReader(index, problems);
    const op = reader(one, read);
    if (op !== null && read.ok) ops.push(op);
  });
  return problems.length > 0 ? { ok: false, problems } : { ok: true, ops };
};

/* ------------------------------------------------------------- the writer */

/** A field as the wire carries it: each of its trees flat, and every line field's too. */
const fieldOnWire = <F extends { formula?: SetupDraftFormula; startsWith?: SetupDraftFormula; showWhen?: SetupDraftFormula }>(field: F): Record<string, unknown> => {
  const { formula, startsWith, showWhen, ...rest } = field;
  const lines = (rest as { lines?: { fields?: unknown } }).lines;
  return {
    ...rest,
    ...(formula === undefined ? {} : { formula: flattenDraftFormula(formula) }),
    ...(startsWith === undefined ? {} : { startsWith: flattenDraftFormula(startsWith) }),
    ...(showWhen === undefined ? {} : { showWhen: flattenDraftFormula(showWhen) }),
    ...(lines === undefined || !Array.isArray(lines.fields) ? {} : { lines: { ...lines, fields: (lines.fields as StudioFieldInput[]).map(fieldOnWire) } }),
  };
};

const fillOnWire = (fill: StudioLinkFill): Record<string, unknown> => (fill.kind === 'formula' ? { ...fill, formula: flattenDraftFormula(fill.formula) } : fill);

/**
 * One strict op as the wire carries it: `readStudioOps`'s inverse, so
 * `readStudioOps(studioOpsOnWire(ops))` reads back `ops`. Every op a client
 * builds (a check's fix, a direct edit) is strict, holding its formulas and a
 * board card's sum as trees; the wire holds them flat, so a batch is put
 * through this on its way to `POST …/ops`.
 */
export const studioOpOnWire = (op: StudioOp): Record<string, unknown> => {
  switch (op.op) {
    case 'add_record':
      return { ...op, fields: op.fields.map(fieldOnWire) };
    case 'add_field':
      return { ...op, field: fieldOnWire(op.field) };
    case 'update_field':
      return { ...op, set: fieldOnWire(op.set) };
    case 'add_link':
      return { ...op, link: { ...op.link, fills: op.link.fills.map(fillOnWire) } };
    case 'update_link':
      return op.fills === undefined ? op : { ...op, fills: op.fills.map(fillOnWire) };
    case 'add_board_card': {
      const { total, ...card } = op.card;
      return { ...op, card: { ...card, ...flattenBoardCardTotal(total) } };
    }
    default:
      return op;
  }
};

/** A batch of strict ops as the wire carries it (`studioOpOnWire`). */
export const studioOpsOnWire = (ops: readonly StudioOp[]): Record<string, unknown>[] => ops.map(studioOpOnWire);
